import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Spin,
  Typography,
  Space,
  message,
  Tabs,
  Badge,
} from 'antd';
import {
  QuestionCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  MessageOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import inquiryService from '../../services/inquiryService';
import { exportToExcel } from '../../utils/excelExport';
import './InquiryManagement.scss';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const InquiryManagement = () => {
  const { t } = useTranslation();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [responseLoading, setResponseLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('PENDING');
  const [form] = Form.useForm();

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const data = await inquiryService.getAdminInquiries();
      setInquiries(data);
    } catch (error) {
      message.error(t('admin.inquiries.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (inquiry) => {
    setSelectedInquiry(inquiry);
    form.setFieldsValue({
      status: inquiry.status,
      admin_response: inquiry.admin_response || '',
    });
    setModalVisible(true);
  };

  const handleSubmitResponse = async () => {
    try {
      const values = await form.validateFields();
      setResponseLoading(true);

      await inquiryService.updateInquiry(selectedInquiry.id, values);
      message.success(t('admin.inquiries.updateSuccess'));
      setModalVisible(false);
      fetchInquiries();
    } catch (error) {
      message.error(t('admin.inquiries.updateFailed'));
    } finally {
      setResponseLoading(false);
    }
  };

  const getStatusTag = (status) => {
    const config = {
      PENDING: { color: 'orange', icon: <ClockCircleOutlined />, text: t('admin.inquiries.statuses.pending') },
      IN_PROGRESS: { color: 'blue', icon: <SyncOutlined spin />, text: t('admin.inquiries.statuses.inProgress') },
      RESOLVED: { color: 'green', icon: <CheckCircleOutlined />, text: t('admin.inquiries.statuses.resolved') },
      CLOSED: { color: 'default', icon: <CheckCircleOutlined />, text: t('admin.inquiries.statuses.closed') },
    };
    const statusConfig = config[status] || config.PENDING;
    return (
      <Tag color={statusConfig.color} icon={statusConfig.icon}>
        {statusConfig.text}
      </Tag>
    );
  };

  const getTypeTag = (type) => {
    const config = {
      PRODUCT: { color: 'blue', text: t('admin.inquiries.types.product') },
      SERVICE: { color: 'green', text: t('admin.inquiries.types.service') },
      GENERAL: { color: 'purple', text: t('admin.inquiries.types.general') },
    };
    const typeConfig = config[type] || config.GENERAL;
    return <Tag color={typeConfig.color}>{typeConfig.text}</Tag>;
  };

  const columns = [
    {
      title: t('common.date'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      render: (date) => new Date(date).toLocaleDateString('el-GR'),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: t('admin.inquiries.user'),
      key: 'user',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.user_name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.user_email}</Text>
        </Space>
      ),
    },
    {
      title: t('admin.inquiries.type'),
      dataIndex: 'inquiry_type',
      key: 'inquiry_type',
      width: 100,
      render: (type) => getTypeTag(type),
      filters: [
        { text: t('admin.inquiries.types.product'), value: 'PRODUCT' },
        { text: t('admin.inquiries.types.service'), value: 'SERVICE' },
        { text: t('admin.inquiries.types.general'), value: 'GENERAL' },
      ],
      onFilter: (value, record) => record.inquiry_type === value,
    },
    {
      title: t('admin.inquiries.subject'),
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: t('admin.inquiries.relatedTo'),
      key: 'related',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.product_name && <Text type="secondary">{t('admin.inquiries.types.product')}: {record.product_name}</Text>}
          {record.service_name && <Text type="secondary">{t('admin.inquiries.types.service')}: {record.service_name}</Text>}
          {record.partner_name && <Text type="secondary">{t('admin.inquiries.partner')}: {record.partner_name}</Text>}
          {!record.product_name && !record.service_name && !record.partner_name && (
            <Text type="secondary">-</Text>
          )}
        </Space>
      ),
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => getStatusTag(status),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Button
          type="primary"
          icon={<MessageOutlined />}
          onClick={() => handleOpenModal(record)}
        >
          {t('admin.inquiries.respond')}
        </Button>
      ),
    },
  ];

  const filterByStatus = (status) => {
    if (status === 'ALL') return inquiries;
    return inquiries.filter((i) => i.status === status);
  };

  const getStatusCount = (status) => {
    return inquiries.filter((i) => i.status === status).length;
  };

  const handleExportExcel = () => {
    const exportColumns = [
      { key: 'id', header: 'ID' },
      { key: 'created_at', header: 'Date', transform: (val) => new Date(val).toLocaleDateString('el-GR') },
      { key: 'user_name', header: 'User' },
      { key: 'user_email', header: 'Email' },
      { key: 'inquiry_type_display', header: 'Type' },
      { key: 'subject', header: 'Subject' },
      { key: 'message', header: 'Message' },
      { key: 'product_name', header: 'Product' },
      { key: 'service_name', header: 'Service' },
      { key: 'partner_name', header: 'Partner' },
      { key: 'status_display', header: 'Status' },
      { key: 'admin_response', header: 'Admin Response' },
      { key: 'responded_at', header: 'Responded At', transform: (val) => val ? new Date(val).toLocaleDateString('el-GR') : '' },
    ];

    exportToExcel(filterByStatus(activeTab), exportColumns, 'inquiries');
  };

  if (loading) {
    return (
      <div className="inquiry-management loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="inquiry-management">
      <div className="page-header">
        <Title level={2}>
          <QuestionCircleOutlined /> {t('admin.inquiries.title')}
        </Title>
        <Button
          icon={<FileExcelOutlined />}
          onClick={handleExportExcel}
        >
          {t('admin.exportExcel')}
        </Button>
      </div>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane
            tab={
              <span>
                <Badge count={getStatusCount('PENDING')} offset={[10, 0]}>
                  <ClockCircleOutlined /> {t('admin.inquiries.statuses.pending')}
                </Badge>
              </span>
            }
            key="PENDING"
          >
            <Table
              dataSource={filterByStatus('PENDING')}
              columns={columns}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <Badge count={getStatusCount('IN_PROGRESS')} offset={[10, 0]}>
                  <SyncOutlined /> {t('admin.inquiries.statuses.inProgress')}
                </Badge>
              </span>
            }
            key="IN_PROGRESS"
          >
            <Table
              dataSource={filterByStatus('IN_PROGRESS')}
              columns={columns}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                <CheckCircleOutlined /> {t('admin.inquiries.statuses.resolved')}
              </span>
            }
            key="RESOLVED"
          >
            <Table
              dataSource={filterByStatus('RESOLVED')}
              columns={columns}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </TabPane>

          <TabPane
            tab={<span>{t('admin.inquiries.all')} ({inquiries.length})</span>}
            key="ALL"
          >
            <Table
              dataSource={filterByStatus('ALL')}
              columns={columns}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Response Modal */}
      <Modal
        title={
          <Space>
            <MessageOutlined />
            <span>{t('admin.inquiries.respondToInquiry')}</span>
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSubmitResponse}
        okText={t('admin.inquiries.saveResponse')}
        confirmLoading={responseLoading}
        width={700}
      >
        {selectedInquiry && (
          <div className="inquiry-response-form">
            <div className="inquiry-info">
              <div className="info-row">
                <Text strong>{t('admin.inquiries.from')}:</Text>
                <Text>{selectedInquiry.user_name} ({selectedInquiry.user_email})</Text>
              </div>

              <div className="info-row">
                <Text strong>{t('admin.inquiries.type')}:</Text>
                {getTypeTag(selectedInquiry.inquiry_type)}
              </div>

              <div className="info-row">
                <Text strong>{t('admin.inquiries.subject')}:</Text>
                <Text>{selectedInquiry.subject}</Text>
              </div>

              {selectedInquiry.product_name && (
                <div className="info-row">
                  <Text strong>{t('admin.inquiries.types.product')}:</Text>
                  <Text>{selectedInquiry.product_name}</Text>
                </div>
              )}

              {selectedInquiry.service_name && (
                <div className="info-row">
                  <Text strong>{t('admin.inquiries.types.service')}:</Text>
                  <Text>{selectedInquiry.service_name}</Text>
                </div>
              )}

              {selectedInquiry.partner_name && (
                <div className="info-row">
                  <Text strong>{t('admin.inquiries.partner')}:</Text>
                  <Text>{selectedInquiry.partner_name}</Text>
                </div>
              )}

              {selectedInquiry.contact_email && (
                <div className="info-row">
                  <Text strong>{t('admin.inquiries.altEmail')}:</Text>
                  <Text>{selectedInquiry.contact_email}</Text>
                </div>
              )}

              {selectedInquiry.contact_phone && (
                <div className="info-row">
                  <Text strong>{t('common.phone')}:</Text>
                  <Text>{selectedInquiry.contact_phone}</Text>
                </div>
              )}

              <div className="message-box">
                <Text strong>{t('admin.inquiries.message')}:</Text>
                <Paragraph>{selectedInquiry.message}</Paragraph>
              </div>
            </div>

            <Form form={form} layout="vertical">
              <Form.Item
                name="status"
                label={t('common.status')}
                rules={[{ required: true, message: t('admin.inquiries.selectStatus') }]}
              >
                <Select>
                  <Option value="PENDING">{t('admin.inquiries.statuses.pending')}</Option>
                  <Option value="IN_PROGRESS">{t('admin.inquiries.statuses.inProgress')}</Option>
                  <Option value="RESOLVED">{t('admin.inquiries.statuses.resolved')}</Option>
                  <Option value="CLOSED">{t('admin.inquiries.statuses.closed')}</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="admin_response"
                label={t('admin.inquiries.yourResponse')}
              >
                <TextArea
                  rows={5}
                  placeholder={t('admin.inquiries.responsePlaceholder')}
                  maxLength={5000}
                  showCount
                />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default InquiryManagement;
